import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { ORDER_HISTORY_ROUTE } from "../../../modules/constants";
import { MessageScreen } from "modules/controls/components/MessageScreen";
import { fetchUser as fetchUserCreator } from "modules/userContext/userContext.actions";
import { fetchOrder as fetchOrderCreator } from "modules/order/order.actions";
import { getOrderHOF } from "modules/order/order.selectors";
import { sendGaPurchase, sendGaPageView } from "modules/analytics/ga";
import {
  buildPurchasePayload,
  alreadySent,
  markSent
} from "modules/analytics/purchase";

const SuccessPageBody =
  window.i2290settings && window.i2290settings.SuccessPageBody;

// Poll for finalization: up to MAX_FETCH_ATTEMPTS re-fetches, FETCH_RETRY_MS apart (~7.5s window),
// to wait out the rare case where finalization commits just after this page loads.
const MAX_FETCH_ATTEMPTS = 5;
const FETCH_RETRY_MS = 1500;

const PaymentSuccessScreen = ({ history, fetchUser, fetchOrder, order, orderId }) => {
  const fetchAttemptsRef = useRef(0);

  useEffect(() => {
    fetchUser();
    // Virtual page_view (G4): the SPA emits none on client-side navigation, so /success is invisible
    // to GA4 without this. Fires for EVERY arrival (paid + free), independent of the purchase gate,
    // so the page is visible/targetable for audiences.
    sendGaPageView("/success");
    // ALWAYS refetch: the store copy is the PRE-payment snapshot (OrderStatus "00", null amount) left
    // over from checkout. The finalized values (OrderStatus "10" + TransactionAmount) live only in the
    // DB, so pull them fresh; the retry effect below waits out a finalization that lands late.
    if (orderId) fetchOrder(orderId);
    // only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for finalization. The confirm/webhook commit usually lands before this page, but in the rare
  // orphaned case (client never confirmed; webhook finalizes moments later) the first fetch can still
  // read "00". Re-fetch until OrderStatus === "10", then stop — bounded so we never spin. Each store
  // update re-runs this; the counter is a ref so bumping it doesn't itself re-trigger the effect. The
  // purchase effect below fires once the order flips to "10".
  useEffect(() => {
    if (!orderId || !order) return; // nothing loaded yet
    if (order.OrderStatus === "10") return; // finalized — done
    if (fetchAttemptsRef.current >= MAX_FETCH_ATTEMPTS) return; // give up (finalization still pending)
    const timer = setTimeout(() => {
      fetchAttemptsRef.current += 1;
      fetchOrder(orderId);
    }, FETCH_RETRY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderId]);

  // Fire the GA4 `purchase` exactly once, once the completed order is available. Runs when the order
  // loads (null -> loaded), and is guarded by the ledger so a refresh/revisit never re-fires.
  useEffect(() => {
    if (!orderId || !order || order.OrderStatus !== "10") return;
    // Paid-only gate (G5): free / $0 / unlimited filings reach /success too — don't count them.
    if (Number(order.TransactionAmount) > 0 && !alreadySent(orderId)) {
      sendGaPurchase(buildPurchasePayload(order, orderId));
      markSent(orderId);
    }
  }, [order, orderId]);

  const handleClickOK = () => {
    history.push(ORDER_HISTORY_ROUTE);
  };

  return (
    <MessageScreen onClickOK={handleClickOK}>
      <div
        className="mb-gutter"
        dangerouslySetInnerHTML={{ __html: SuccessPageBody }}
      />
    </MessageScreen>
  );
};

const mapStateToProps = (state, ownProps) => {
  // /success uses ?orderId= — distinct from the ?order= that getOrderIdFromUrl reads elsewhere.
  const orderId = new URLSearchParams(ownProps.location.search).get("orderId");
  return {
    orderId,
    order: orderId ? getOrderHOF(orderId)(state) : null
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchUser: () => dispatch(fetchUserCreator()),
  fetchOrder: orderId => dispatch(fetchOrderCreator(orderId)),
});

export const PaymentSuccessScreenContainer = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PaymentSuccessScreen)
);
